import { getAnalytics } from "@tm/insurance/analytics";
import { trackEvent } from "@tm/insurance/analytics/xdm";
import {
  consumePostReloadPaxToastCookie,
  findTravellerIndexByCheckoutId,
  reorderTravellersBySelectionOrder,
} from "@tm/insurance/utils";
import { constants } from "@tm/isomorphic-utils";
import { usePlanType } from "@tm/ui";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { ClientError } from "@tm/utility";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FormProvider,
  SubmitErrorHandler,
  useFieldArray,
  useForm,
  useWatch,
  type UseFormReturn,
} from "react-hook-form";
import type { ITravellerDetailsFormV2 } from "../AddTravellerDetail/type";
import WebToast from "../Toast/WebToast";
import { TRAVELLER_DETAILS_COMPONENT_MAPPER } from "./constants/componentMapper";
import { TravellerDetailsV2Provider } from "./contexts/TravellerDetailsV2Context";
import { useTravellerSubmit } from "./hooks/useTravellerSubmit";
import { useTravellerValidation } from "./hooks/useTravellerValidation";
import { ListContent, PageRoot, ScrollArea, WebToastWrap } from "./Styles";
import type {
  TravellerDetailsV2WithFormProps,
  TravellerSecondaryFieldFlags,
  TravellerV2Component,
} from "./TravellerDetailsV2.types";

const SCROLL_ELEVATION_THRESHOLD = 8;

function extractPrimaryAndTncDefaults(components: TravellerV2Component[]): {
  primaryContactData: { email: string; mobile: string };
  tncs: boolean[];
} {
  let primary = { email: "", mobile: "" };
  let tncLen = 1;
  for (const c of components) {
    const d = (c.data || {}) as Record<string, unknown>;
    const pc = d["primaryContact"] as
      | { fields?: { mobile?: { value?: string }; email?: { value?: string } } }
      | undefined;
    if (pc?.fields) {
      primary = {
        mobile: pc.fields.mobile?.value ?? "",
        email: pc.fields.email?.value ?? "",
      };
    }
    const tnc = d["tnc"] as string[] | undefined;
    if (Array.isArray(tnc) && tnc.length > 0) {
      tncLen = tnc.length;
    }
  }
  return {
    primaryContactData: primary,
    tncs: Array.from({ length: tncLen }, () => true),
  };
}

type InnerProps = TravellerDetailsV2WithFormProps & {
  methods: UseFormReturn<ITravellerDetailsFormV2>;
};

function TravellerDetailsV2FormInner({
  components,
  pageData,
  methods,
  ...props
}: InnerProps) {
  const {
    onBuy: onBuyProp,
    onDownloadTemplate,
    onUploadTravellerFile,
    onAddTravellerManually,
    loading: externalLoading,
    saveFormValuesOnBlur,
    handleCountryCodeModal,
    selectedCountry,
    usePrimaryContactCardUi = true,
    onBack,
    onEditTrip,
    onPlanEdit,
    className,
    firstErrorIndex,
    setFirstErrorIndex,
    onSetError,
    serverErrors,
    checkoutError,
    getSelectedCountry,
  } = props;

  const { control, getValues, setValue, setError, trigger, reset } = methods;

  const travellerFa = useFieldArray({ control, name: "travellerData" });

  const { type } = usePlanType();
  const device = useBreakpointV2();
  const analytics = getAnalytics();

  const { travellerCount, errorToasts } = pageData || {};
  const {
    formErrorToast: pageFormErrorToast,
    addErrorToast: pageAddErrorToast,
  } = (errorToasts || {}) as {
    formErrorToast?: { message?: string };
    addErrorToast?: { message?: string };
  };
  const { formErrorToast, addErrorToast } = constants;

  const [isFieldError, setIsFieldError] = useState(false);

  const travellerDataWatch = useWatch({ control, name: "travellerData" });
  const selectedCount = useMemo(
    () => travellerDataWatch?.filter((t) => t?.isSelected)?.length ?? 0,
    [travellerDataWatch]
  );

  const [expandedTravellerIndex, setExpandedTravellerIndex] = useState(-1);
  const [uploadedExcelFile, setUploadedExcelFile] = useState<File | null>(null);
  const [forceShowAllTravellers, setForceShowAllTravellers] = useState(false);
  const [scrollElevated, setScrollElevated] = useState(false);
  const [paxReloadToastMsg, setPaxReloadToastMsg] = useState<string | null>(
    null
  );

  const primaryContactRef = useRef<HTMLDivElement>(null);
  const tncRef = useRef<HTMLDivElement>(null);
  const travellerSectionRef = useRef<HTMLDivElement>(null);
  const secondaryContactFieldsRef = useRef<TravellerSecondaryFieldFlags>({});

  const handleErrorToast = useCallback(() => {
    setTimeout(() => setIsFieldError(false), 3000);
  }, []);

  const { scrollToErrorTraveller, filterTravellers, collectTravellerErrors } =
    useTravellerValidation(
      { getValues, reset, setError, trigger },
      setForceShowAllTravellers
    );

  if (checkoutError && typeof window !== "undefined") {
    throw new ClientError(checkoutError);
  }

  useEffect(() => {
    const msg = consumePostReloadPaxToastCookie();
    if (msg) setPaxReloadToastMsg(msg);
  }, []);

  useEffect(() => {
    if (!paxReloadToastMsg) return;
    const t = window.setTimeout(() => setPaxReloadToastMsg(null), 4500);
    return () => window.clearTimeout(t);
  }, [paxReloadToastMsg]);

  useEffect(() => {
    if (firstErrorIndex !== undefined && firstErrorIndex >= 0) {
      setExpandedTravellerIndex(firstErrorIndex);
      scrollToErrorTraveller(firstErrorIndex, 300);
    }
    setFirstErrorIndex?.(-1);
  }, [firstErrorIndex, setFirstErrorIndex, scrollToErrorTraveller]);

  useEffect(() => {
    if (onSetError) {
      setError(onSetError.errTrype as any, onSetError.errName);
    }
  }, [onSetError, setError]);

  useEffect(() => {
    if (!serverErrors?.length) return;

    const travellersWithErrors: {
      index: number;
      errors: { field: string; message: string }[];
    }[] = [];

    serverErrors.forEach((item) => {
      const { name, dob, gender, passport, nomineeName } =
        item.displayError || {};
      const errs: { field: string; message: string }[] = [];
      if (name) errs.push({ field: "name", message: name });
      if (dob) errs.push({ field: "dob", message: dob });
      if (gender) errs.push({ field: "gender", message: gender });
      if (passport) errs.push({ field: "passport", message: passport });
      if (nomineeName)
        errs.push({ field: "nomineeName", message: nomineeName });
      if (errs.length) {
        const currentData = getValues("travellerData") || [];
        const index = findTravellerIndexByCheckoutId(
          currentData,
          item.travellerId
        );
        if (index >= 0) {
          travellersWithErrors.push({
            index,
            errors: errs,
          });
        }
      }
    });

    if (travellersWithErrors.length === 0) return;

    const currentData = getValues("travellerData");
    if (!currentData) return;

    const errorIndices = travellersWithErrors.map((t) => t.index);
    const newArray: typeof currentData = [];
    errorIndices.forEach((idx) => {
      if (currentData[idx]) newArray.push(currentData[idx]);
    });
    currentData.forEach((traveller, idx) => {
      if (!errorIndices.includes(idx)) newArray.push(traveller);
    });

    reset(
      { ...getValues(), travellerData: newArray },
      { keepDefaultValues: true }
    );

    setTimeout(() => {
      travellersWithErrors.forEach((trav, newIndex) => {
        trav.errors.forEach((err) => {
          setError(`travellerData.${newIndex}.${err.field}` as any, {
            message: err.message,
          });
        });
      });
      setExpandedTravellerIndex(0);
      setIsFieldError(true);
      handleErrorToast();
      scrollToErrorTraveller(0, 350);
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply when serverErrors batch changes
  }, [serverErrors]);

  const handleTncCheckbox = useCallback(
    (index: number) => {
      const cur = getValues(`tncs.${index}`);
      analytics?.trackTravDetailsEvents({
        actionType: `disclaimer_${cur ? "unchecked" : "checked"}`,
      });
      setValue(`tncs.${index}`, !cur);
      trigger(`tncs.${index}`);
    },
    [analytics, getValues, setValue, trigger]
  );

  const onSubmitError: SubmitErrorHandler<ITravellerDetailsFormV2> =
    useCallback(
      async (errors) => {
        setIsFieldError(true);
        handleErrorToast();
        const tdErr = errors.travellerData;
        if (Array.isArray(tdErr) && tdErr.length > 0) {
          const selected: number[] = [];
          const errorArr: { index: number; errorType: string }[] = [];
          getValues("travellerData")?.forEach((item, index) => {
            if (item?.isSelected) selected.push(index);
          });
          tdErr.forEach((travellerError, index) => {
            if (!travellerError) return;
            const fields = [
              "name",
              "dob",
              "gender",
              "passport",
              "nomineeName",
            ] as const;
            const errorType = fields.find((field) => travellerError[field]);
            if (errorType && selected.includes(index)) {
              errorArr.push({ index, errorType });
            }
          });
          if (errorArr.length > 0) {
            await filterTravellers(selected, errorArr);
            setExpandedTravellerIndex(0);
            setFirstErrorIndex?.(0);
            scrollToErrorTraveller(0, 350);
            return;
          }
        } else if (
          errors.primaryContactData?.email ||
          errors.primaryContactData?.mobile
        ) {
          setTimeout(() => {
            primaryContactRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "end",
              inline: "center",
            });
          }, 150);
        } else if (errors.tncs?.length) {
          setTimeout(() => {
            tncRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "center",
            });
          }, 150);
        }
      },
      [
        handleErrorToast,
        scrollToErrorTraveller,
        setFirstErrorIndex,
        filterTravellers,
        getValues,
      ]
    );

  const { localSubmitting, submitForm } = useTravellerSubmit({
    methods: { handleSubmit: methods.handleSubmit },
    components,
    uploadedExcelFile,
    onBuyProp,
    getSelectedCountry,
    selectedCountry,
    onSubmitError,
  });

  const isLoading = Boolean(externalLoading) || localSubmitting;

  const handleBuy = useCallback(async () => {
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "buy_clicked",
      },
    });
    analytics?.trackTravDetailsEvents(
      { actionType: "buy_clicked" },
      "pageView",
      type
    );

    const tc = travellerCount ?? 0;

    if (tc > 0 && selectedCount !== tc) {
      setIsFieldError(true);
      setExpandedTravellerIndex(-1);
      scrollToErrorTraveller(0, 300);
      handleErrorToast();
      return;
    }

    setForceShowAllTravellers(true);
    await new Promise((r) => setTimeout(r, 100));

    const { selected, errorArr } = collectTravellerErrors(
      secondaryContactFieldsRef.current
    );

    await filterTravellers(selected, errorArr);

    if (errorArr.length > 0) {
      setExpandedTravellerIndex(0);
      setFirstErrorIndex?.(0);
      setIsFieldError(true);
      handleErrorToast();
      scrollToErrorTraveller(0, 350);
      return;
    }

    const formSnapshot = getValues();
    reset(
      {
        ...formSnapshot,
        travellerData: reorderTravellersBySelectionOrder(
          formSnapshot.travellerData || []
        ),
      },
      { keepDefaultValues: true }
    );
    await new Promise((r) => setTimeout(r, 50));

    submitForm();
  }, [
    analytics,
    type,
    travellerCount,
    selectedCount,
    scrollToErrorTraveller,
    handleErrorToast,
    setFirstErrorIndex,
    collectTravellerErrors,
    filterTravellers,
    submitForm,
    getValues,
    reset,
  ]);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollElevated(e.currentTarget.scrollTop > SCROLL_ELEVATION_THRESHOLD);
  };

  const keyFor = (item: TravellerV2Component, index: number) =>
    `${item.id}-${index}`;

  const tc = travellerCount ?? 0;

  return (
    <TravellerDetailsV2Provider
      pageData={pageData}
      onBack={onBack}
      onEditTrip={onEditTrip}
      onPlanEdit={onPlanEdit}
      onBuy={handleBuy}
      onDownloadTemplate={onDownloadTemplate}
      onUploadTravellerFile={onUploadTravellerFile}
      onAddTravellerManually={onAddTravellerManually}
      scrollElevated={scrollElevated}
      isLoading={isLoading}
      saveFormValuesOnBlur={saveFormValuesOnBlur}
      handleCountryCodeModal={handleCountryCodeModal}
      selectedCountry={selectedCountry}
      usePrimaryContactCardUi={usePrimaryContactCardUi}
      travellerFieldArray={travellerFa}
      expandedTravellerIndex={expandedTravellerIndex}
      setExpandedTravellerIndex={setExpandedTravellerIndex}
      forceShowAllTravellers={forceShowAllTravellers}
      setForceShowAllTravellers={setForceShowAllTravellers}
      setUploadedExcelFile={setUploadedExcelFile}
      secondaryContactFieldsRef={secondaryContactFieldsRef}
      handleTncCheckbox={handleTncCheckbox}
      primaryContactRef={primaryContactRef}
      tncRef={tncRef}
      travellerSectionRef={travellerSectionRef}
    >
      <PageRoot className={className}>
        <ScrollArea onScroll={onScroll}>
          <div ref={travellerSectionRef}>
            <ListContent>
              {components.map((item, index) => {
                const Cmp = TRAVELLER_DETAILS_COMPONENT_MAPPER[item.id];
                if (!Cmp) return null;
                const raw = (item.data || {}) as Record<string, unknown>;
                return <Cmp key={keyFor(item, index)} {...raw} />;
              })}
            </ListContent>
          </div>
        </ScrollArea>
      </PageRoot>

      {isFieldError ? (
        <WebToastWrap>
          <WebToast
            type="error"
            position="fixed"
            right="50px"
            bottom={device === "sm" ? "70px" : "50px"}
            toastTxt={
              selectedCount === tc && tc > 0
                ? pageFormErrorToast?.message || formErrorToast
                : pageAddErrorToast?.message || addErrorToast
            }
            color="#6B4300"
          />
        </WebToastWrap>
      ) : null}
      {paxReloadToastMsg ? (
        <WebToastWrap>
          <WebToast
            type="success"
            position="fixed"
            right="50px"
            bottom={device === "sm" ? "70px" : "50px"}
            toastTxt={paxReloadToastMsg}
            color="#0d3b2e"
          />
        </WebToastWrap>
      ) : null}
    </TravellerDetailsV2Provider>
  );
}

export function TravellerDetailsV2WithForm(
  props: TravellerDetailsV2WithFormProps
) {
  const { components, pageData, ...rest } = props;
  const { primaryContactData, tncs } = useMemo(
    () => extractPrimaryAndTncDefaults(components),
    [components]
  );

  const methods = useForm<ITravellerDetailsFormV2>({
    defaultValues: {
      travellerData: [],
      primaryContactData,
      gstInfoDetails: { gstNumber: "", companyDetails: "" },
      tncs,
    },
    mode: "onTouched",
  });

  return (
    <FormProvider {...methods}>
      <TravellerDetailsV2FormInner
        components={components}
        pageData={pageData}
        methods={methods}
        {...rest}
      />
    </FormProvider>
  );
}
